"use client";
import React, { type JSX } from "react";
import { Heading } from "@/components/ui/typography";
import { CardContent } from "../common/card";
import SideNotchCard from "../common/SideNotchCard";
import Image from "next/image";

const steps = [
  {
    number: "1",
    title: "Month 1 After Incorporation Anniversary",
    description: (
      <>
        <p className="flex">
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Annual Return filing deadline window opens.
        </p>
      </>
    ),
    icon: "/icon.png",
    alignment: "right",
  },
  {
    number: "2",
    title: <span className="leading-[29px] pt-2"> Month 12</span>,
    description: (
      <>
        <p className="flex items-start">
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Annual Return filing deadline window opens.
        </p>
      </>
    ),
    icon: "/download-step.png",
    alignment: "left",
  },
  {
    number: "3",
    title: <span>Month 12–18</span>,
    description: (
      <>
        <p className="inline-flex">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          First Profits Tax Return issued by IRD.
        </p>
        <p className="flex">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Audit preparation begins.
        </p>
      </>
    ),
    icon: "/icon.png",
    alignment: "right",
  },
  {
    number: "4",
    title: "After Audit Completion",
    description: (
      <>
        <p className="flex items-start">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Profits Tax Return submitted with audited financial statements.
        </p>
      </>
    ),
    icon: "/marked-step.png", // Ensure these paths exist
    alignment: "left",
  },
  {
    number: "5",
    title: "Ongoing Throughout the Year",
    description: (
      <>
        <p className="flex pb-2">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Bookkeeping maintained
        </p>
        <p className="flex py-2">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Financials prepared
        </p>
        <p className="flex py-2">
          {" "}
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Transactions categorized
        </p>
        <p className="flex py-2">
          <Image
            src={"/list-check.svg"}
            width="20"
            height="20"
            alt="list-check"
            className="me-2 mt-1"
          />
          Offshore documentation preserved
        </p>
      </>
    ),
    icon: "/icon.png", // Ensure these paths exist
    alignment: "right",
  },
];

export const FlowChart = ({ heading = "Hong Kong Company Compliance Calendar" }: { heading?: string } = {}): JSX.Element => {
  return (
    <section className="relative w-full py-8 md:py-[60px] flex flex-col gap-[52px]">
      <div>
        <Heading as="h2" variant="sectionCentered">
          {heading}
        </Heading>
        <p className="font-inter text-white/60 text-[18px] text-center font-medium">
          Instead of guessing what&apos;s due when, here&apos;s how a typical
          compliance cycle works.
        </p>
      </div>

      <div className="relative w-full max-w-[1440px] mx-auto px-4">
        <div className="relative min-h-[1400px] md:min-h-[1250px]">
          {/* Main Vertical Timeline Line */}
          <div className="absolute left-1/2 top-0 w-1 h-full bg-[linear-gradient(180deg,rgba(58,118,255,1)_0%,rgba(58,118,255,1)_70%,rgba(0,0,0,1)_100%)] -translate-x-1/2" />

          {steps.map((step, index) => {
            // Vertical offset spacing
            const topOffset = index * 250; // Controls the gap between steps

            return (
              <div
                key={index}
                className="absolute w-full"
                style={{ top: `${topOffset}px` }}
              >
                {/* Step Circle Number */}
                {/* <div className="absolute left-1/2 -translate-x-1/2 w-16 h-16 bg-white rounded-full overflow-hidden shadow-[0px_4px_4px_#004eff26] z-30">
                  <div className="absolute top-[60px] left-1/2 -translate-x-1/2 w-16 h-16 bg-[#004eff] rounded-full blur-[17px]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-medium text-[#004eff] text-[36.6px] text-center leading-[37px]">
                    {step.number}
                  </div>
                </div> */}

                {/* Floating Icon */}
                <Image
                  className={`absolute w-[108px] h-[87px] z-40 sm:block hidden  ${
                    step.alignment === "right"
                      ? "left-[calc(50%+20px)]"
                      : "right-[calc(50%+20px)]"
                  }`}
                  width={108}
                  height={87}
                  alt="Icon"
                  src={step.icon}
                />

                {/* Notch Card */}
                <SideNotchCard
                  notch={{
                    width: { base: -20, sm: -20, md: 50, lg: 50 },
                    depth: { base: 0, sm: 0, md: 50, lg: 50 },
                    topLeft: 30,
                    topRight: 0,
                    bottomLeft: 20,
                    bottomRight: 20,
                  }}
                  widthClass="w-[350px]"
                  heightClass={step.alignment === "right" ? `${220}` : `${170}`}
                  borderColor="bg-blue-200"
                  shadowColor="#6a98ff"
                  borderWidth={2}
                  className={`absolute top-4 ${
                    step.alignment === "right"
                      ? "left-1/2 -translate-x-1/2 md:left-[calc(50%+63px)] md:-translate-x-0"
                      : "left-1/2 -translate-x-1/2 md:left-auto md:right-[calc(50%+63px)] md:-translate-x-0 transform scale-x-[-1]"
                  } border-0 shadow-none bg-transparent`}
                >
                  <CardContent
                    className={`relative flex flex-col gap-3 ${step.alignment === "left" && "scale-x-[-1]"}`}
                  >
                    <h3
                      className={`h-auto flex ${
                        step.alignment === "right"
                          ? " sm:ps-12 ps-0"
                          : "justify-start"
                      } font-medium text-white text-[20px] leading-[1.2] pt-4 whitespace-pre-wrap`}
                    >
                      {step.title}
                    </h3>

                    <div className="flex w-full items-center gap-2.5 text-start">
                      <div className="relative w-fit font-normal text-white/70 text-base lg:text-[15px] leading-6">
                        {step.description}
                      </div>
                    </div>
                  </CardContent>
                </SideNotchCard>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
